import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import useToast, { EToastType } from "@/hooks/ui/useToast";

import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface ConfirmationDialogProps {
  nullAmount?: number;
  setIsNullConfirmationOpen: (open: boolean) => void;
  handleRemoveNulls: () => void;
  handleHighlightInvalidatedGeometry: () => void;
}

export default function ConfirmationNullDialog({
  nullAmount,
  setIsNullConfirmationOpen,
  handleRemoveNulls,
  handleHighlightInvalidatedGeometry
}: ConfirmationDialogProps) {
  const { showToast } = useToast();

  const handleKeepNulls = () => {
    showToast({
      type: EToastType.SUCCESS,
      message: (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-bold text-txtHeading">
            Data Setting Saved
          </p>
          <p className="text-sm text-txtHeading">
            Country, Spatial Aggregation and Type successfully added.{" "}
            {nullAmount} rows with null have been retained.
          </p>
        </div>
      )
    });
    setIsNullConfirmationOpen(false);
    handleHighlightInvalidatedGeometry();
  };

  return (
    <DialogContent
      className="flex flex-col items-center gap-0"
      aria-describedby="null-cells-detected"
      isHideClose
    >
      <VisuallyHidden>
        <DialogTitle>Null Cells Detected</DialogTitle>
        <DialogDescription>
          We found null cells in {nullAmount} rows. Would you like to remove
          them?{" "}
        </DialogDescription>
      </VisuallyHidden>

      <h1 className="text-txtHeading text-xl font-semibold leading-7 mb-2">
        Null Cells Detected
      </h1>
      <p className="text-txtBody text-center font-normal text-sm leading-[22px]">
        We found null cells in {nullAmount} rows. Would you like to remove them?
      </p>

      <div className="flex flex-row gap-3 w-full mt-6">
        <Button
          variant="outline"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          onClick={handleKeepNulls}
          aria-label="Keep rows with null values"
        >
          Keep
        </Button>

        <Button
          variant="default"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          onClick={handleRemoveNulls}
          aria-label="Remove rows with null values"
        >
          Remove
        </Button>
      </div>
    </DialogContent>
  );
}
